import React, { useState } from "react";
import { Alert, Tag, Empty, Modal } from "antd";
import {
  ScanOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ArrowRightOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";
import { getMatchImageUrl } from "./faceSearchResults.utils";
import { AI_THEME, AiStyles } from "./faceSearchTheme";

const CompareImagePanel = ({ title, subtitle, imageUrl, name, meta }) => (
  <div style={comparePanelStyle}>
    <div style={comparePanelTitle}>{title}</div>
    {subtitle ? <div style={comparePanelSubtitle}>{subtitle}</div> : null}
    <div style={compareImageWrap}>
      {imageUrl ? (
        <img src={imageUrl} alt={name || title} style={compareImage} />
      ) : (
        <div style={compareImageFallback}>
          <UserOutlined style={{ fontSize: 36, color: "#b7c3d9" }} />
        </div>
      )}
    </div>
    {name ? <div style={compareName}>{name}</div> : null}
    {meta ? <div style={compareMeta}>{meta}</div> : null}
  </div>
);

export const FaceSearchResultsView = ({
  status,
  statusMessage = "",
  inputImageUrl,
  matchResults = [],
  onMatchClick,
}) => {
  const [imagePreviewModal, setImagePreviewModal] = useState({
    open: false,
    type: "query",
    match: null,
  });

  const failed = status === "Failure";
  const noMatchMessage =
    status ||
    (failed
      ? "No Match Found"
      : "AI completed analysis but no similar faces met the confidence threshold.");

  const openQueryImagePreview = () => {
    if (!inputImageUrl) {
      return;
    }
    setImagePreviewModal({ open: true, type: "query", match: null });
  };

  const openMatchImagePreview = (match) => {
    if (!match) {
      return;
    }
    setImagePreviewModal({ open: true, type: "match", match });
  };

  const closeImagePreview = () => {
    setImagePreviewModal({ open: false, type: "query", match: null });
  };

  const previewMatch = imagePreviewModal.match;
  const previewMatchImageUrl = previewMatch
    ? getMatchImageUrl(previewMatch)
    : "";
  const previewMatchScore = previewMatch
    ? `${parseFloat(previewMatch?.score || 0).toFixed(2)}%`
    : "";
  const isQueryPreview = imagePreviewModal.type === "query";

  return (
  <div className="face-search-results" style={shellStyle}>
    <AiStyles />
    <style>{`
      .face-search-results {
        box-sizing: border-box;
      }

      .face-search-results-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 16px;
        padding: 14px 16px;
        border-radius: 12px;
        background: ${AI_THEME.primaryBg};
        border: 1px solid ${AI_THEME.cardBorder};
      }

      .face-search-results-body {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
        align-items: start;
      }

      .face-search-results-query,
      .face-search-results-matches {
        background: #fafcff;
        border: 1px solid ${AI_THEME.cardBorder};
        border-radius: 12px;
        padding: 16px;
        min-width: 0;
      }

      .face-search-results-panel-title {
        color: ${AI_THEME.primary};
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 12px;
      }

      .face-search-results-panel-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 12px;
      }

      .face-search-results-panel-title-row .face-search-results-panel-title {
        margin-bottom: 0;
      }

      .face-search-results-query-image {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        border-radius: 12px;
        background: #fff;
        border: 2px dashed ${AI_THEME.cardBorder};
        padding: 12px;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
      }

      .face-search-results-query-image img {
        max-width: 100%;
        max-height: 220px;
        object-fit: contain;
        border-radius: 10px;
        box-shadow: 0 8px 20px rgba(31, 63, 127, 0.1);
      }

      .face-search-results-query-hint {
        margin-top: 10px;
        color: ${AI_THEME.muted};
        font-size: 12px;
        line-height: 1.5;
      }

      .face-search-results-matches-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
        gap: 12px;
      }

      .face-search-results-match-card {
        cursor: pointer;
        background: #fff;
        border: 1px solid ${AI_THEME.cardBorder};
        border-radius: 12px;
        padding: 10px;
        transition: all 0.22s ease;
        box-shadow: 0 4px 14px rgba(31, 63, 127, 0.06);
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .face-search-results-match-card:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 24px rgba(31, 63, 127, 0.12);
        border-color: ${AI_THEME.primary};
      }

      .face-search-results-match-image-wrap {
        position: relative;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 10px;
        aspect-ratio: 4 / 5;
        background: #f0f4fa;
      }

      .face-search-results-match-image-wrap img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .face-search-results-match-fallback {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .face-search-results-match-score {
        position: absolute;
        top: 8px;
        right: 8px;
        background: ${AI_THEME.primary};
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        padding: 3px 8px;
        border-radius: 4px;
      }

      .face-search-results-match-zoom {
        position: absolute;
        right: 8px;
        bottom: 8px;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(31, 63, 127, 0.82);
        color: #fff;
        font-size: 13px;
      }

      .face-search-results-match-name {
        color: ${AI_THEME.text};
        font-weight: 700;
        font-size: 13px;
        line-height: 1.35;
        min-height: 36px;
        word-break: break-word;
      }

      .face-search-results-match-meta {
        color: ${AI_THEME.muted};
        font-size: 11px;
        margin-top: 2px;
      }

      .face-search-results-match-action {
        margin-top: auto;
        padding-top: 8px;
        color: ${AI_THEME.primary};
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      @media (min-width: 700px) {
        .face-search-results-body {
          grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
        }

        .face-search-results-matches-grid {
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        }
      }

      @media (min-width: 1100px) {
        .face-search-results {
          padding: 18px;
        }

        .face-search-results-matches-grid {
          grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
          gap: 14px;
        }

        .face-search-results-query-image img {
          max-height: 260px;
        }
      }

      @media (min-width: 1400px) {
        .face-search-results-body {
          grid-template-columns: minmax(200px, 240px) minmax(0, 1fr);
        }

        .face-search-results-matches-grid {
          grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
        }
      }

      @media (max-width: 699px) {
        .face-search-results {
          padding: 12px;
        }

        .face-search-results-header {
          padding: 12px 14px;
        }

        .face-search-results-matches-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
    `}</style>

    <div className="face-search-results-header">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={headerIcon}>
          <ScanOutlined />
        </div>
        <div>
          <div style={headerTitle}>AI Face Search Results</div>
          <div style={headerSubtitle}>Biometric similarity analysis report</div>
        </div>
      </div>
      <Tag
        icon={failed ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
        color={failed ? "error" : "success"}
        style={{ margin: 0, padding: "5px 12px", fontSize: 13, borderRadius: 8 }}
      >
        {status || (failed ? "FAILURE" : "SUCCESS")}
      </Tag>
    </div>

    <div className="face-search-results-body">
      <div className="face-search-results-query">
        <div className="face-search-results-panel-title">Query Image</div>
        <div
          className="face-search-results-query-image"
          style={{ cursor: inputImageUrl ? "pointer" : "default" }}
          onClick={() => inputImageUrl && openQueryImagePreview()}
          onKeyDown={(event) => {
            if (inputImageUrl && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              openQueryImagePreview();
            }
          }}
          role={inputImageUrl ? "button" : undefined}
          tabIndex={inputImageUrl ? 0 : undefined}
          title={inputImageUrl ? "Click to view full image" : undefined}
        >
          {inputImageUrl ? (
            <>
              <img src={inputImageUrl} alt="Query" />
              <span style={zoomHint}>
                <ZoomInOutlined /> Click to enlarge
              </span>
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No image" />
          )}
        </div>
        <div className="face-search-results-query-hint">
          Source photo used for AI facial embedding and database matching.
        </div>
      </div>

      <div className="face-search-results-matches">
        <div className="face-search-results-panel-title-row">
          <span className="face-search-results-panel-title">Matched Profiles</span>
          <Tag color="blue" style={{ borderRadius: 8, margin: 0 }}>
            {matchResults.length} found
          </Tag>
        </div>

        {(status === "Success" || status === "SUCCESS") && matchResults.length > 0 ? (
          <div className="face-search-results-matches-grid">
            {matchResults.map((match, index) => {
              const matchImageUrl = getMatchImageUrl(match);
              const matchScore = `${parseFloat(match?.score || 0).toFixed(2)}%`;

              return (
                <div
                  key={match.criminal_id || `match-${index}`}
                  className="face-search-results-match-card"
                  onClick={() => onMatchClick && onMatchClick(match.criminal_id)}
                >
                  <div
                    className="face-search-results-match-image-wrap"
                    style={{ cursor: "pointer" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      openMatchImagePreview(match);
                    }}
                    title="Click to view image"
                  >
                    {matchImageUrl ? (
                      <img src={matchImageUrl} alt={match.name || "Match"} />
                    ) : (
                      <div className="face-search-results-match-fallback">
                        <UserOutlined style={{ fontSize: 28, color: "#b7c3d9" }} />
                      </div>
                    )}
                    <span className="face-search-results-match-score">{matchScore}</span>
                    <span className="face-search-results-match-zoom">
                      <ZoomInOutlined />
                    </span>
                  </div>

                  <div className="face-search-results-match-name">
                    {match.name || "Unknown"}
                  </div>
                  <div className="face-search-results-match-meta">
                    ID: {match.criminal_id || "-"}
                  </div>

                  <div className="face-search-results-match-action">
                    View Profile <ArrowRightOutlined style={{ fontSize: 11 }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Alert
            type={failed ? "warning" : "info"}
            showIcon
            message={failed ? "No match found" : "No matches found"}
            style={{ borderRadius: 10 }}
          />
        )}
      </div>
    </div>

    <Modal
      title={isQueryPreview ? "Query Image" : "Matched Profile"}
      visible={imagePreviewModal.open}
      onCancel={closeImagePreview}
      footer={null}
      width={Math.min(560, window.innerWidth - 32)}
      centered
      destroyOnClose
      bodyStyle={{ padding: "20px 24px 24px" }}
    >
      {isQueryPreview ? (
        <CompareImagePanel
          title="Query Image"
          subtitle="Uploaded search photo"
          imageUrl={inputImageUrl}
        />
      ) : (
        <CompareImagePanel
          title="Matched Profile"
          subtitle={`Similarity score: ${previewMatchScore}`}
          imageUrl={previewMatchImageUrl}
          name={previewMatch?.name}
          meta={`ID: ${previewMatch?.criminal_id || "-"}`}
        />
      )}
    </Modal>
  </div>
  );
};

const shellStyle = {
  width: "100%",
  background: AI_THEME.card,
  borderRadius: AI_THEME.radius,
  padding: "14px",
  border: `1px solid ${AI_THEME.cardBorder}`,
  boxShadow: AI_THEME.shadow,
  animation: "aiFadeUp 0.4s ease",
};

const headerIcon = {
  width: 42,
  height: 42,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: AI_THEME.primary,
  color: "#fff",
  fontSize: 18,
};

const headerTitle = { color: AI_THEME.text, fontWeight: 600, fontSize: 17 };
const headerSubtitle = { color: AI_THEME.muted, fontSize: 12, marginTop: 2 };

const zoomHint = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginTop: 10,
  fontSize: 12,
  color: AI_THEME.primary,
  fontWeight: 600,
};

const comparePanelStyle = {
  height: "100%",
  background: "#fafcff",
  border: `1px solid ${AI_THEME.cardBorder}`,
  borderRadius: 12,
  padding: 16,
};

const comparePanelTitle = {
  color: AI_THEME.primary,
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 4,
};

const comparePanelSubtitle = {
  color: AI_THEME.muted,
  fontSize: 12,
  marginBottom: 12,
};

const compareImageWrap = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 280,
  borderRadius: 12,
  background: "#fff",
  border: `1px solid ${AI_THEME.cardBorder}`,
  padding: 12,
};

const compareImage = {
  maxWidth: "100%",
  maxHeight: 320,
  objectFit: "contain",
  borderRadius: 10,
};

const compareImageFallback = {
  width: "100%",
  minHeight: 280,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f0f4fa",
  borderRadius: 10,
};

const compareName = {
  marginTop: 12,
  color: AI_THEME.text,
  fontWeight: 700,
  fontSize: 15,
};

const compareMeta = {
  marginTop: 4,
  color: AI_THEME.muted,
  fontSize: 12,
};
